import * as angular from 'angular';

import { ComponentDefinition, IGridsterOptions, DefaultGridsterOptions, IUserModel } from '../../../models';
import { IUserService, IAccountService, ILoggerService, IStoreService } from '../../../services';
import { isEditMode, toggleEditMode, guidEmpty } from '../../../common';
import { IRootScope } from 'IRootScope';
import { StoreElement } from '../../../stateManagement';

class UsersStateController implements angular.IComponentController, angular.IOnInit, angular.IOnDestroy {
    controlUserDetails: any;
    currentUserName: string;
    formController: angular.IFormController;
    gridsterOptions: IGridsterOptions;
    importUserModalId: string;
    isEditMode: boolean;
    isLoading: boolean;
    loadingMessage: string;
    selectedItem: IUserModel;
    userList: IUserModel[];

    private _garbages: (() => void)[];
    private _saveErrorMessage = 'Save or cancel changes before selecting another item';

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$translate',
        'userService',
        'accountService',
        'loggerService',
        'storeService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$state: ng.ui.IStateService,
        private _$translate: angular.translate.ITranslateService,
        private _userService: IUserService,
        private _accountService: IAccountService,
        private _loggerService: ILoggerService,
        private _storeService: IStoreService
    ) { }

    $onInit() {
        this._garbages = [];
        this.importUserModalId = 'ImportUserCsvModal';
        this.initialize();

        this._garbages.push(this._storeService.register(
            store => this.currentUserName = store.authentication.value.userName,
            [StoreElement.Authentication]
        ));

        this._garbages.push(this._$scope.$on(toggleEditMode, () => {
            this.setEditMode(this._$rootScope.isEditMode);
        }));

        this._garbages.push(this._$scope.$watch(() => this.selectedItem, (newValue) => {
            if (newValue === null) { // unselecting
                this.userList = this.userList.filter(u => !(u as any).adding);
            }
        }));

        this._garbages.push(this._$scope.$on(isEditMode, this.onIsEditMode(true, false)));
    }

    $onDestroy(): void {
        if (this._garbages?.length) {
            this._garbages.forEach(g => g());
        }
    }


    /** Initialize user state and get all users */
    private initialize(): void {
        this.isLoading = true;
        this.isEditMode = false;
        this.gridsterOptions = angular.copy(DefaultGridsterOptions);
        this.refresh();
    }

    /**
     * Select a user and view setected userDetails
     * @param selectedUser
     * @param $event
     */
    selectItem(selectedUser: IUserModel, $event): void {
        if (this._$rootScope.isEditMode) {
            // Edit user in contextual menu
            if (!this.formController || !this.formController.$dirty) {
                this.selectedItem = selectedUser;
            } else {
                this._loggerService.logWarning('There are pending changes. Save or cancel before selecting another user.');
            }
            $event.preventDefault();
            // View user details
        } else if (selectedUser) {
            this._$state.go('user', {
                user: selectedUser,
                userName: selectedUser.userName,
                userId: selectedUser.guid
            });
        }
    }

    unselect($event) {
        if (!$event.isDefaultPrevented()) {
            // Edit user in contextual menu
            if (!this.formController || !this.formController.$dirty) {
                this.selectedItem = null;
            } else {
                const msg = this._$translate.instant(this._saveErrorMessage);
                this._loggerService.logWarning(msg);
                $event.stopPropagation();
            }
        }
    }

    /**
     * Delete a user
     * @param selectedItem
     */
    deleteUser(selectedItem: IUserModel) {
        this._accountService
            .deleteOne(selectedItem.guid)
            .then(() => {
                const index = this.userList.indexOf(selectedItem, 0);
                if (index > -1) {
                    this.userList.splice(index, 1);
                    this.userList = this.userList.map(d => d);
                }
                this._loggerService.logSuccess('User successfully deleted.');
                this.selectedItem = undefined;
            })
            .catch(reason => {
                this._loggerService.logError(
                    'User is in use and cannot be deleted.',
                );
            });
    }

    /** Create a new user */
    drop(userToAdd) {
        // Upload CSV user file
        if (userToAdd && userToAdd.itemId === 'uploadUserFile') {
            angular.element(`#${this.importUserModalId}`).modal('show');
            // Add a user (from domain or not)
        } else {
            const user: IUserModel = {
                applicationCount: 0,
                applications: [],
                domainName: '',
                roles: [],
                userName: 'User',
                firstName: '',
                lastName: '',
                email: '',
                pictureUrl: '',
                phoneNumber: '',
                password: '',
                confirmPassword: '',
                isSuperAdmin: false,
                guid: guidEmpty,
                addFromDomain: false,
                canDelete: true,
                name: undefined
            };
            (user as any).adding = true;
            if (userToAdd.itemId === 'addDomainUser') {
                user.addFromDomain = true;
            }
            this.userList = this.userList.concat([user]);
            this.selectedItem = user;
        }
    }

    onIsEditMode(draggable: boolean = true, resizable: boolean = true) {
        return (event, argument) => {
            if (argument === true) {
                this.gridsterOptions.draggable.enabled = draggable;
                this.gridsterOptions.resizable.enabled = resizable;
            } else {
                this.selectedItem = null;
                this.gridsterOptions.draggable.enabled = false;
                this.gridsterOptions.resizable.enabled = false;
            }
            this.isEditMode = argument;
        };
    }

    refresh(): void {
        this._userService
            .getAllUserModelsWithDetails(true, true)
            .then(userList => {
                this.userList = userList;
                this.isLoading = false;
            });
    }

    private setEditMode(isEditMode: boolean): void {
        this.isEditMode = isEditMode;
    }
}

export const wtUsersState: ComponentDefinition = {
    componentId: 'wtUsersState',
    controller: UsersStateController,
    template: require('./usersState.component.html'),
    controllerAs: 'vm',
    bindings: {},
};